import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faCalendarDay,
	faCarSide,
	faCheckCircle,
	faExchangeAlt,
	faFlagCheckered,
	faHandHoldingHeart,
	faMoon,
	faRoad,
	faShieldAlt,
	faStopwatch,
	faTrophy,
	faTruckPickup,
	faUmbrellaBeach,
	faWrench,
} from "@fortawesome/free-solid-svg-icons"
import {
	T,
	TAILLE,
	course,
	iconeDiag,
	iconeTravaux,
	jourMois,
	nombre,
	phraseCourse,
	pluriel,
} from "./ecranUtils"

// Pages de l'écran salle de pause. Grammaire unique : un chiffre de la
// semaine qui court contre son propre record, sur une barre qui se remplit
// jusqu'au record (une rangée d'icônes débordait dès qu'il y en avait trop,
// retour Jordan 01.10). Règle de
// l'amendement 01.10 : un bloc sans donnée n'est pas affiché (jamais de mur
// de « 0 ») et une page vide sort de la rotation. Pas de <table> (CSS global
// legacy d'App.css sur th/td/tr).

const TRANSITION_COULEUR = "color 600ms ease"
// La barre se remplit en douceur quand un chiffre bouge : le seul mouvement
// non déclenché par un geste, il signale une vraie arrivée.
const TRANSITION_BARRE = "width 600ms ease, left 600ms ease"

// ─── Barre de course ───────────────────────────────────────────────────────

// La course au record en une barre. Fond gris = ce qu'il reste pour égaler
// le record (la fin de la barre EST le record tant qu'il tient) ; noir =
// fait ; bleu = fait aujourd'hui ; or = au-delà de l'ancien record.
const Barre = ({ actuel, precedent, aujourdhui = 0, hauteur }) => {
	const max = Math.max(actuel, precedent)
	if (max <= 0) return null
	const base = precedent > 0 ? Math.min(actuel, precedent) : actuel
	// Le jour est en bleu, sauf au-delà du record (or).
	const jour = Math.max(0, Math.min(base, base - actuel + aujourdhui))
	// Positions dans la partie remplie (elle-même = actuel / max de la piste).
	const dans = (v) => `${actuel ? (v * 100) / actuel : 0}%`
	const segments = [
		{ cle: "fait", debut: 0, long: base - jour, couleur: T.encre },
		{ cle: "jour", debut: base - jour, long: jour, couleur: T.bleu },
		{ cle: "record", debut: base, long: actuel - base, couleur: T.or },
	]
	return (
		<div style={{ ...st.barre, height: hauteur }} aria-hidden="true">
			<div
				style={{
					...st.barreRemplie,
					width: `${(actuel * 100) / max}%`,
				}}
			>
				{segments.map((g) =>
					g.long > 0 ? (
						<span
							key={g.cle}
							style={{
								...st.barreSegment,
								left: dans(g.debut),
								width: dans(g.long),
								backgroundColor: g.couleur,
							}}
						/>
					) : null,
				)}
			</div>
		</div>
	)
}

// ─── Course au record ──────────────────────────────────────────────────────

// Le chiffre, sa course contre le record et la phrase qui la dit.
// `quandRecord` : quand le record a été établi (absent s'il vient d'être
// battu : l'ancien n'a plus de date utile).
const Course = ({
	actuel,
	unite,
	precedent,
	quandRecord,
	aujourdhui = 0,
	secondaire = false,
	zeroTexte = null,
}) => {
	const c = course(actuel, precedent)
	const phrase = phraseCourse(actuel, c)
	return (
		<section style={st.course}>
			<div style={st.courseHaut}>
				{actuel === 0 && zeroTexte ? (
					// Une série qui repart : on l'annonce plutôt qu'un « 0 ».
					<div style={st.zero}>{zeroTexte}</div>
				) : (
					<div style={st.heros}>
						<span
							style={{
								...st.herosChiffre,
								fontSize: secondaire
									? TAILLE.moyen
									: TAILLE.heros,
								color: c.fete ? T.or : T.encre,
							}}
						>
							{nombre(actuel)}
						</span>
						<span style={st.herosUnite}>{unite}</span>
					</div>
				)}
				{c.precedent > 0 ? (
					<div style={st.record}>
						<FontAwesomeIcon
							icon={faTrophy}
							style={st.recordIcone}
						/>
						<div>
							<div style={st.recordValeur}>
								{c.etat === "battu"
									? "Ancien record"
									: "Record"}
								{" : "}
								{nombre(c.precedent)}
							</div>
							{c.etat !== "battu" && quandRecord ? (
								<div style={st.recordQuand}>{quandRecord}</div>
							) : null}
						</div>
					</div>
				) : null}
			</div>
			<Barre
				actuel={actuel}
				precedent={c.precedent}
				aujourdhui={aujourdhui}
				hauteur={secondaire ? "3.2vh" : "4.8vh"}
			/>
			{phrase || aujourdhui > 0 ? (
				<p style={st.phrase}>
					{phrase ? (
						<span
							style={
								c.fete
									? { color: T.or, fontWeight: 700 }
									: undefined
							}
						>
							{phrase}
						</span>
					) : null}
					{aujourdhui > 0 ? (
						<span style={{ color: T.bleu }}>
							{phrase ? " " : ""}
							Dont {nombre(aujourdhui)} aujourd'hui.
						</span>
					) : null}
				</p>
			) : null}
		</section>
	)
}

// ─── Briques ───────────────────────────────────────────────────────────────

const TitrePage = ({ children }) => <h1 style={st.titrePage}>{children}</h1>

const Panneau = ({ titre, children }) => (
	<section style={st.panneau}>
		<h2 style={st.panneauTitre}>{titre}</h2>
		{children}
	</section>
)

// Faits secondaires en pied de page : un chiffre en gras, une phrase courte.
const Pied = ({ faits }) =>
	faits.length ? (
		<footer style={st.pied}>
			{faits.map((f, i) => (
				<div
					key={f.cle}
					style={{
						...st.fait,
						borderLeft: i ? `1px solid ${T.trait}` : "none",
						paddingLeft: i ? "2vw" : 0,
					}}
				>
					<FontAwesomeIcon
						icon={f.icone}
						fixedWidth
						style={{
							...st.faitIcone,
							color: f.couleur || T.discret,
						}}
					/>
					<span style={st.faitTexte}>
						<strong style={st.faitFort}>{f.fort}</strong> {f.texte}
					</span>
				</div>
			))}
		</footer>
	) : null

const PIED_MAX = 4

const jalon = (j, quoi) =>
	j && j.reste
		? `, ${quoi} ${nombre(j.prochain)}e dans ${nombre(j.reste)}`
		: ""

const semaineDu = (iso) => (iso ? `semaine du ${jourMois(iso)}` : null)

// ─── Atelier ───────────────────────────────────────────────────────────────

// Un type de travaux : son nom et sa barre vers le record de la semaine,
// le chiffre et le record à droite.
const LigneTravaux = ({ m }) => {
	const r = m.record_semaine || {}
	const c = course(m.semaine, r.precedent)
	return (
		<div style={st.travaux}>
			<FontAwesomeIcon
				icon={iconeTravaux(m.nom)}
				fixedWidth
				style={st.travauxIcone}
			/>
			<div style={st.travauxMilieu}>
				<span style={st.travauxNom}>{m.nom}</span>
				<Barre
					actuel={m.semaine}
					precedent={c.precedent}
					hauteur="1.6vh"
				/>
			</div>
			<strong style={{ ...st.travauxNb, color: c.fete ? T.or : T.encre }}>
				{c.fete ? (
					<FontAwesomeIcon
						icon={faTrophy}
						style={st.travauxTrophee}
					/>
				) : null}
				{nombre(m.semaine)}
			</strong>
			<span style={st.travauxRecord}>
				{c.precedent > 0 ? (
					<>
						<FontAwesomeIcon
							icon={faTrophy}
							style={{ color: T.or }}
						/>{" "}
						{nombre(c.precedent)}
					</>
				) : null}
			</span>
		</div>
	)
}

const faitsAtelier = (a, f, s) => {
	const faits = []
	if (a.accueillis.annee > 0)
		faits.push({
			cle: "annee",
			icone: faFlagCheckered,
			fort: nombre(a.accueillis.annee),
			texte: `${pluriel(a.accueillis.annee, "véhicule", "véhicules")} depuis janvier${jalon(a.jalon, "le")}`,
		})
	if (s.remorques_repares.actif && s.remorques_repares.mois > 0)
		faits.push({
			cle: "repares",
			icone: faTruckPickup,
			fort: nombre(s.remorques_repares.mois),
			texte: `${pluriel(s.remorques_repares.mois, "remorqué puis réparé", "remorqués puis réparés")} chez nous ce mois`,
		})
	if (f.entretien.flotte > 0) {
		const tout = f.entretien.a_jour === f.entretien.flotte
		faits.push({
			cle: "entretien",
			icone: tout ? faCheckCircle : faWrench,
			couleur: tout ? T.vert : undefined,
			fort: `${nombre(f.entretien.a_jour)} sur ${nombre(f.entretien.flotte)}`,
			texte: "de nos véhicules à jour d'entretien",
		})
	}
	if (f.interventions_mois > 0)
		faits.push({
			cle: "flotte",
			icone: faWrench,
			fort: nombre(f.interventions_mois),
			texte: `${pluriel(f.interventions_mois, "intervention", "interventions")} sur notre flotte ce mois`,
		})
	if (s.location.sur_la_route > 0)
		faits.push({
			cle: "location",
			icone: faCarSide,
			fort: nombre(s.location.sur_la_route),
			texte: `${pluriel(s.location.sur_la_route, "véhicule", "véhicules")} de location sur la route`,
		})
	if (s.remplacements_mois > 0)
		faits.push({
			cle: "remplacement",
			icone: faExchangeAlt,
			fort: nombre(s.remplacements_mois),
			texte: `${pluriel(s.remplacements_mois, "véhicule", "véhicules")} de remplacement ce mois`,
		})
	if (s.km_clients_mois > 0)
		faits.push({
			cle: "km",
			icone: faRoad,
			fort: nombre(s.km_clients_mois),
			texte: "km parcourus par nos clients ce mois",
		})
	return faits.slice(0, PIED_MAX)
}

const MIX_ATELIER_MAX = 6

const PageAtelier = ({ a, f, s }) => {
	const acc = a.accueillis
	const rec = a.record_semaine || {}
	// Seulement les travaux faits cette semaine : jamais de lignes à 0.
	const mix = a.mix
		.filter((m) => m.semaine > 0)
		.sort((x, y) => y.semaine - x.semaine || y.mois - x.mois)
		.slice(0, MIX_ATELIER_MAX)
	return (
		<div style={st.page}>
			<TitrePage>L'atelier cette semaine</TitrePage>
			<Course
				actuel={acc.semaine}
				unite={pluriel(
					acc.semaine,
					"véhicule accueilli",
					"véhicules accueillis",
				)}
				precedent={rec.precedent}
				quandRecord={semaineDu(rec.date)}
				aujourdhui={acc.jour}
			/>
			{mix.length ? (
				<Panneau titre="Travaux de la semaine">
					<div style={st.grilleTravaux}>
						{mix.map((m) => (
							<LigneTravaux key={m.nom} m={m} />
						))}
					</div>
				</Panneau>
			) : (
				<div style={st.vide} />
			)}
			<Pied faits={faitsAtelier(a, f, s)} />
		</div>
	)
}

// ─── Dépannage ─────────────────────────────────────────────────────────────

const Pastille = ({ icone, nb, libelle }) => (
	<div style={st.pastille}>
		<FontAwesomeIcon icon={icone} fixedWidth style={st.pastilleIcone} />
		<strong style={st.pastilleNb}>{nombre(nb)}</strong>
		<span style={st.pastilleLibelle}>{libelle}</span>
	</div>
)

// Carte de la rangée du bas : un titre, un chiffre, son contexte.
const Carte = ({ titre, children }) => (
	<section style={st.carte}>
		<h2 style={st.panneauTitre}>{titre}</h2>
		{children}
	</section>
)

const ChiffreCarte = ({ valeur, unite, couleur = T.encre }) => (
	<div style={st.carteChiffreLigne}>
		<strong style={{ ...st.carteChiffre, color: couleur }}>{valeur}</strong>
		<span style={st.carteUnite}>{unite}</span>
	</div>
)

// Délai moyen d'arrivée, toutes missions : la semaine en cours, sinon la
// précédente (une semaine qui commence n'a pas encore de moyenne).
const CarteArrivee = ({ delai }) => {
	if (!delai) return null
	const enCours = delai.semaine.nb > 0
	const sem = enCours ? delai.semaine : delai.semaine_precedente
	if (!sem.nb) return null
	const prec = delai.semaine_precedente
	return (
		<Carte titre="Arrivée sur place">
			<ChiffreCarte
				valeur={nombre(sem.moyenne_min)}
				unite="min en moyenne"
			/>
			<p style={st.carteTexte}>
				du coup de téléphone à l'arrivée,{" "}
				{enCours ? "cette semaine" : "la semaine dernière"}, sur{" "}
				{nombre(sem.nb)} {pluriel(sem.nb, "mission", "missions")}
			</p>
			{enCours && prec.nb > 0 ? (
				<p style={st.carteTexteDiscret}>
					Semaine dernière : {nombre(prec.moyenne_min)} min
				</p>
			) : null}
		</Carte>
	)
}

// L'état réel du moment (pas la semaine) : missions et camions engagés.
const CarteMaintenant = ({ live }) => {
	if (!live || !live.actif) return null
	const nb = live.missions_en_cours
	const camions = live.camions
	return (
		<Carte titre="En ce moment">
			{nb > 0 ? (
				<>
					<ChiffreCarte
						valeur={nombre(nb)}
						unite={pluriel(
							nb,
							"mission en cours",
							"missions en cours",
						)}
						couleur={T.bleu}
					/>
					{camions.en_mission > 0 ? (
						<p style={st.carteTexte}>
							{nombre(camions.en_mission)}{" "}
							{pluriel(camions.en_mission, "camion", "camions")}{" "}
							sur {nombre(camions.flotte)} sur la route
						</p>
					) : null}
				</>
			) : (
				<p style={st.carteTexte}>Aucune mission en cours.</p>
			)}
		</Carte>
	)
}

const MIX_DEPANNAGE_MAX = 6

const faitsDepannage = (d) => {
	const faits = []
	if (d.missions.annee > 0)
		faits.push({
			cle: "annee",
			icone: faFlagCheckered,
			fort: nombre(d.missions.annee),
			texte: `${pluriel(d.missions.annee, "mission", "missions")} depuis janvier${jalon(d.jalon, "la")}`,
		})
	const rj = d.record_jour || {}
	const cj = course(rj.actuel || 0, rj.precedent)
	if (cj.etat === "battu" && cj.fete)
		faits.push({
			cle: "record_jour",
			icone: faTrophy,
			couleur: T.or,
			fort: nombre(rj.actuel),
			texte: "missions aujourd'hui : meilleure journée jamais vue !",
		})
	else if (cj.precedent > 0)
		// Valeur ET date du record courant (serveur) : battu sous le seuil de
		// fête, il est daté d'aujourd'hui — avant, l'ancienne valeur s'affichait
		// avec la date du jour.
		faits.push({
			cle: "record_jour",
			icone: faTrophy,
			couleur: T.or,
			fort: nombre(rj.valeur),
			texte: `missions, la meilleure journée (le ${jourMois(rj.date)})`,
		})
	const h = d.heures_ingrates_mois
	if (h.nuit > 0)
		faits.push({
			cle: "nuit",
			icone: faMoon,
			fort: nombre(h.nuit),
			texte: "de nuit ce mois",
		})
	if (h.week_end > 0)
		faits.push({
			cle: "week_end",
			icone: faCalendarDay,
			fort: nombre(h.week_end),
			texte: "le week-end ce mois",
		})
	if (h.ferie > 0)
		faits.push({
			cle: "ferie",
			icone: faUmbrellaBeach,
			fort: nombre(h.ferie),
			texte: "un jour férié ce mois",
		})
	if (d.missions.mois > 0)
		faits.push({
			cle: "mois",
			icone: faTruckPickup,
			fort: nombre(d.missions.mois),
			texte: `${pluriel(d.missions.mois, "mission", "missions")} ce mois`,
		})
	return faits.slice(0, PIED_MAX)
}

const PageDepannage = ({ d, live }) => {
	const rec = d.record_semaine || {}
	const mix = d.mix_semaine
	const pastilles = mix.categories.slice(
		0,
		mix.police > 0 ? MIX_DEPANNAGE_MAX - 1 : MIX_DEPANNAGE_MAX,
	)
	const aInterventions = pastilles.length > 0 || mix.police > 0
	return (
		<div style={st.page}>
			<TitrePage>Le dépannage cette semaine</TitrePage>
			<Course
				actuel={d.missions.semaine}
				unite={pluriel(d.missions.semaine, "mission", "missions")}
				precedent={rec.precedent}
				quandRecord={semaineDu(rec.date)}
				aujourdhui={d.missions.jour}
			/>
			<div style={st.rangeeBas}>
				<Panneau titre="Interventions de la semaine">
					{aInterventions ? (
						<div style={st.pastilles}>
							{pastilles.map((c) => (
								<Pastille
									key={c.nom}
									icone={iconeDiag(c.nom)}
									nb={c.nb}
									libelle={c.nom}
								/>
							))}
							{mix.police > 0 ? (
								<Pastille
									icone={faShieldAlt}
									nb={mix.police}
									libelle="Police"
								/>
							) : null}
						</div>
					) : (
						<p style={st.carteTexte}>
							Pas encore d'intervention cette semaine.
						</p>
					)}
					{d.sur_place.semaine > 0 ? (
						<p style={st.surPlace}>
							<FontAwesomeIcon
								icon={faHandHoldingHeart}
								fixedWidth
								style={{ color: T.vert }}
							/>{" "}
							<strong>{nombre(d.sur_place.semaine)}</strong>{" "}
							{pluriel(
								d.sur_place.semaine,
								"client reparti par ses propres moyens",
								"clients repartis par leurs propres moyens",
							)}{" "}
							après notre passage
						</p>
					) : null}
				</Panneau>
				<CarteArrivee delai={d.delai_arrivee} />
				<CarteMaintenant live={live} />
			</div>
			<Pied faits={faitsDepannage(d)} />
		</div>
	)
}

// ─── Police ────────────────────────────────────────────────────────────────

// Règle 0 → 2 × objectif : la moyenne doit rester à gauche du trait.
const Regle = ({ moyenne, cible }) => {
	const max = Math.max(cible * 2, Math.ceil(moyenne / 10) * 10)
	const pct = (v) => `${(Math.min(v, max) * 100) / max}%`
	const ok = moyenne <= cible
	return (
		<div style={st.regle} aria-hidden="true">
			<span style={st.regleFond} />
			<span
				style={{
					...st.regleZone,
					width: pct(cible),
				}}
			/>
			<span style={{ ...st.regleCible, left: pct(cible) }} />
			<span style={{ ...st.regleCibleTexte, left: pct(cible) }}>
				objectif {nombre(cible)} min
			</span>
			<span
				style={{
					...st.regleMoyenne,
					left: pct(moyenne),
					backgroundColor: ok ? T.vert : T.orange,
				}}
			/>
			<span style={{ ...st.regleBorne, left: 0 }}>0</span>
			<span style={{ ...st.regleBorne, right: 0 }}>
				{nombre(max)} min
			</span>
		</div>
	)
}

const faitsPolice = (p, enCours) => {
	const faits = []
	const prec = p.semaine_precedente
	if (enCours && prec.nb > 0)
		faits.push({
			cle: "precedente",
			icone: faStopwatch,
			fort: `${nombre(prec.moyenne_min)} min`,
			texte: `en moyenne la semaine dernière, ${nombre(prec.pct_dans_cible)} % dans l'objectif`,
		})
	const m = p.meilleure_semaine
	if (m)
		faits.push({
			cle: "meilleure",
			icone: faTrophy,
			couleur: T.or,
			fort: `${nombre(m.moyenne_min)} min`,
			texte: m.battu
				? "meilleure semaine jusqu'ici, battue cette semaine !"
				: `meilleure moyenne d'une semaine, celle du ${jourMois(m.lundi)}`,
		})
	return faits
}

const PagePolice = ({ p }) => {
	// Semaine en cours si elle a des interventions, sinon la précédente.
	const enCours = p.semaine.nb > 0
	const sem = enCours ? p.semaine : p.semaine_precedente
	const ok = sem.nb > 0 && sem.moyenne_min <= p.cible_min
	const serie = p.serie
	const quand = enCours ? "cette semaine" : "la semaine dernière"
	return (
		<div style={st.page}>
			<TitrePage>
				La police : arriver en moins de {nombre(p.cible_min)} minutes
			</TitrePage>
			<div style={st.colonnes}>
				{sem.nb > 0 ? (
					<section style={st.course}>
						<div style={st.heros}>
							<span
								style={{
									...st.herosChiffre,
									fontSize: TAILLE.heros,
									color: ok ? T.vert : T.orange,
								}}
							>
								{nombre(sem.moyenne_min)}
							</span>
							<span style={st.herosUnite}>min en moyenne</span>
						</div>
						<Regle moyenne={sem.moyenne_min} cible={p.cible_min} />
						<p style={st.phrase}>
							Du coup de téléphone à l'arrivée sur place, {quand}.
						</p>
						<p style={st.phraseDiscrete}>
							{nombre(sem.pct_dans_cible)} % à l'heure, sur{" "}
							{nombre(sem.nb)}{" "}
							{pluriel(sem.nb, "intervention", "interventions")}.
						</p>
					</section>
				) : (
					<div />
				)}
				{serie.record > 0 ? (
					<Course
						actuel={serie.en_cours}
						unite={`${pluriel(serie.en_cours, "intervention", "interventions")} d'affilée en moins de ${nombre(p.cible_min)} minutes`}
						precedent={serie.precedent}
						secondaire
						zeroTexte={`Nouvelle série : chaque intervention en moins de ${nombre(p.cible_min)} minutes compte.`}
					/>
				) : (
					<div />
				)}
			</div>
			<div style={st.vide} />
			<Pied faits={faitsPolice(p, enCours)} />
		</div>
	)
}

// ─── Pages disponibles ─────────────────────────────────────────────────────

// Pages à afficher, dans l'ordre, selon les données disponibles.
export const pagesDisponibles = (data) => {
	if (!data) return []
	const out = []
	const d = data.depannage
	if (
		d &&
		d.actif &&
		(d.missions.annee > 0 || (d.record_semaine && d.record_semaine.valeur))
	)
		out.push({
			cle: "depannage",
			nom: "Dépannage",
			rendu: () => (
				<PageDepannage d={d} live={data.live && data.live.depannage} />
			),
		})
	const p = data.police
	if (
		p &&
		p.actif &&
		(p.semaine.nb > 0 || p.semaine_precedente.nb > 0 || p.serie.record > 0)
	)
		out.push({
			cle: "police",
			nom: "Police",
			rendu: () => <PagePolice p={p} />,
		})
	const a = data.atelier
	if (
		a &&
		(a.accueillis.annee > 0 ||
			a.mix.length > 0 ||
			(a.record_semaine && a.record_semaine.valeur))
	)
		out.push({
			cle: "atelier",
			nom: "Atelier",
			rendu: () => <PageAtelier a={a} f={data.flotte} s={data.service} />,
		})
	return out
}

const st = {
	page: {
		height: "100%",
		display: "flex",
		flexDirection: "column",
		gap: "3.2vh",
		boxSizing: "border-box",
	},
	titrePage: {
		margin: 0,
		fontSize: TAILLE.titre,
		fontWeight: 700,
		letterSpacing: "-0.01em",
		lineHeight: 1.1,
		color: T.encre,
	},
	vide: { flex: 1 },

	// Course
	course: {
		display: "flex",
		flexDirection: "column",
		gap: "2.2vh",
	},
	courseHaut: {
		display: "flex",
		flexWrap: "wrap",
		alignItems: "flex-end",
		justifyContent: "space-between",
		gap: "2vh 4vw",
	},
	colonnes: {
		display: "grid",
		gridTemplateColumns: "minmax(0, 1.15fr) minmax(0, 1fr)",
		columnGap: "5vw",
		alignItems: "start",
	},
	heros: {
		display: "flex",
		alignItems: "baseline",
		gap: "1.6vw",
		minWidth: 0,
	},
	herosChiffre: {
		fontWeight: 700,
		lineHeight: 0.82,
		letterSpacing: "-0.045em",
		fontVariantNumeric: "tabular-nums",
		transition: TRANSITION_COULEUR,
	},
	zero: {
		fontSize: TAILLE.grand,
		fontWeight: 700,
		lineHeight: 1.2,
		color: T.encre,
	},
	herosUnite: {
		fontSize: TAILLE.grand,
		fontWeight: 500,
		color: T.encre,
		lineHeight: 1.15,
	},
	record: {
		display: "flex",
		alignItems: "center",
		gap: "1.2vw",
		flex: "0 0 auto",
		paddingBottom: "0.6vh",
	},
	recordIcone: { color: T.or, fontSize: TAILLE.chiffre },
	recordValeur: {
		fontSize: TAILLE.grand,
		fontWeight: 700,
		color: T.encre,
		lineHeight: 1.15,
		fontVariantNumeric: "tabular-nums",
	},
	recordQuand: {
		fontSize: TAILLE.petit,
		color: T.discret,
		lineHeight: 1.3,
	},
	phrase: {
		margin: 0,
		fontSize: TAILLE.grand,
		fontWeight: 500,
		lineHeight: 1.25,
		color: T.encre,
	},
	phraseDiscrete: {
		margin: 0,
		fontSize: TAILLE.texte,
		lineHeight: 1.3,
		color: T.discret,
	},

	// Barre de course
	barre: {
		position: "relative",
		width: "100%",
		borderRadius: 99,
		backgroundColor: T.fantome,
		overflow: "hidden",
	},
	barreRemplie: {
		position: "absolute",
		left: 0,
		top: 0,
		bottom: 0,
		borderRadius: 99,
		overflow: "hidden",
		transition: TRANSITION_BARRE,
	},
	barreSegment: {
		position: "absolute",
		top: 0,
		bottom: 0,
		transition: TRANSITION_BARRE,
	},

	// Panneau
	panneau: {
		flex: 1,
		minHeight: 0,
		boxSizing: "border-box",
		padding: "2.6vh 2.4vw",
		borderRadius: 20,
		backgroundColor: T.surface,
		border: `1px solid ${T.trait}`,
		display: "flex",
		flexDirection: "column",
		gap: "2vh",
		overflow: "hidden",
	},
	panneauTitre: {
		margin: 0,
		fontSize: TAILLE.texte,
		fontWeight: 700,
		color: T.encre,
	},
	grilleTravaux: {
		display: "grid",
		gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
		columnGap: "4vw",
		rowGap: "2vh",
	},
	travaux: {
		display: "flex",
		alignItems: "center",
		gap: "1.2vw",
		minWidth: 0,
	},
	travauxIcone: { fontSize: TAILLE.texte, color: T.discret },
	travauxMilieu: {
		flex: 1,
		minWidth: 0,
		display: "flex",
		flexDirection: "column",
		gap: "0.9vh",
	},
	travauxNom: {
		minWidth: 0,
		fontSize: TAILLE.texte,
		lineHeight: 1.15,
		color: T.encre,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	travauxNb: {
		fontSize: TAILLE.grand,
		fontWeight: 700,
		fontVariantNumeric: "tabular-nums",
		display: "flex",
		alignItems: "center",
		gap: "0.6vw",
	},
	travauxTrophee: { fontSize: TAILLE.petit },
	travauxRecord: {
		flex: "0 0 4.5vw",
		textAlign: "right",
		fontSize: TAILLE.petit,
		color: T.discret,
		fontVariantNumeric: "tabular-nums",
		whiteSpace: "nowrap",
	},
	rangeeBas: {
		flex: 1,
		minHeight: 0,
		display: "grid",
		gridTemplateColumns: "minmax(0, 1.7fr) minmax(0, 1fr) minmax(0, 1fr)",
		columnGap: "2vw",
	},
	carte: {
		minWidth: 0,
		boxSizing: "border-box",
		padding: "2.6vh 1.8vw",
		borderRadius: 20,
		backgroundColor: T.surface,
		border: `1px solid ${T.trait}`,
		display: "flex",
		flexDirection: "column",
		gap: "1.4vh",
		overflow: "hidden",
	},
	carteChiffreLigne: {
		display: "flex",
		alignItems: "baseline",
		flexWrap: "wrap",
		gap: "0.4vh 0.8vw",
	},
	carteChiffre: {
		fontSize: TAILLE.chiffre,
		fontWeight: 700,
		lineHeight: 1,
		letterSpacing: "-0.02em",
		fontVariantNumeric: "tabular-nums",
	},
	carteUnite: {
		fontSize: TAILLE.texte,
		fontWeight: 500,
		color: T.encre,
	},
	carteTexte: {
		margin: 0,
		fontSize: TAILLE.petit,
		lineHeight: 1.35,
		color: T.encre,
	},
	carteTexteDiscret: {
		margin: 0,
		fontSize: TAILLE.petit,
		lineHeight: 1.35,
		color: T.discret,
	},
	pastilles: {
		display: "grid",
		gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
		gap: "1.8vh 2vw",
	},
	pastille: {
		display: "flex",
		alignItems: "baseline",
		gap: "0.8vw",
		minWidth: 0,
	},
	pastilleIcone: {
		fontSize: TAILLE.texte,
		color: T.discret,
		alignSelf: "center",
		flex: "0 0 auto",
	},
	pastilleNb: {
		fontSize: TAILLE.grand,
		fontWeight: 700,
		lineHeight: 1,
		color: T.encre,
		fontVariantNumeric: "tabular-nums",
	},
	pastilleLibelle: {
		fontSize: TAILLE.texte,
		color: T.encre,
		minWidth: 0,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	surPlace: {
		margin: 0,
		fontSize: TAILLE.texte,
		color: T.encre,
	},

	// Règle police
	regle: {
		position: "relative",
		height: "9vh",
		margin: "4vh 0 0",
	},
	regleFond: {
		position: "absolute",
		left: 0,
		right: 0,
		top: "2vh",
		height: "2.4vh",
		borderRadius: 99,
		backgroundColor: T.trait,
	},
	regleZone: {
		position: "absolute",
		left: 0,
		top: "2vh",
		height: "2.4vh",
		borderRadius: "99px 0 0 99px",
		backgroundColor: "rgba(22, 163, 74, 0.18)",
	},
	regleCible: {
		position: "absolute",
		top: "0.4vh",
		height: "5.6vh",
		width: 4,
		marginLeft: -2,
		backgroundColor: T.encre,
	},
	regleCibleTexte: {
		position: "absolute",
		top: "-4vh",
		transform: "translateX(-50%)",
		fontSize: TAILLE.petit,
		fontWeight: 700,
		color: T.encre,
		whiteSpace: "nowrap",
	},
	regleMoyenne: {
		position: "absolute",
		top: "0.8vh",
		width: "4.8vh",
		height: "4.8vh",
		marginLeft: "-2.4vh",
		borderRadius: "50%",
		border: `0.6vh solid ${T.surface}`,
		boxSizing: "border-box",
	},
	regleBorne: {
		position: "absolute",
		top: "5.6vh",
		fontSize: TAILLE.petit,
		color: T.discret,
	},

	// Pied
	pied: {
		display: "flex",
		alignItems: "stretch",
		gap: "2vw",
		padding: "2vh 2vw",
		borderRadius: 16,
		backgroundColor: T.surface,
		border: `1px solid ${T.trait}`,
	},
	fait: {
		flex: "1 1 0",
		minWidth: 0,
		display: "flex",
		alignItems: "center",
		gap: "1vw",
	},
	faitIcone: { fontSize: TAILLE.texte, flex: "0 0 auto" },
	faitTexte: {
		fontSize: TAILLE.petit,
		color: T.discret,
		lineHeight: 1.3,
	},
	faitFort: {
		fontSize: TAILLE.texte,
		fontWeight: 700,
		color: T.encre,
		fontVariantNumeric: "tabular-nums",
	},
}
